// Typed client for the TalentForge backend. Vite proxies /api → :8000 (see vite.config.ts),
// so these are same-origin relative calls. Every request is tenant-scoped.
import ky from 'ky'

const api = ky.create({
  credentials: 'same-origin',
  timeout: 120_000,
  hooks: {
    afterResponse: [(_request, _options, response) => {
      if (response.status === 401 && !window.location.pathname.startsWith('/login')) {
        window.location.assign(`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`)
      }
    }],
  },
})

export interface AuthUser {
  id: string
  email: string
  name: string
}

export interface AuthTenant {
  id: string
  slug: string
  name: string
}

export interface AuthMe {
  user: AuthUser
  tenant: AuthTenant
}

export const login = (email: string, password: string): Promise<AuthMe> =>
  api.post('/api/auth/login', { json: { email, password } }).json<AuthMe>()

export const logout = (): Promise<{ ok: boolean }> => api.post('/api/auth/logout').json<{ ok: boolean }>()

export const getMe = (): Promise<AuthMe> => api.get('/api/users/me').json<AuthMe>()

export interface UploadResult {
  name: string
  status: 'queued' | 'duplicate' | 'skipped'
  kind?: string
  rows?: number
  reason?: string
}

export async function uploadFiles(files: File[]): Promise<{ uploaded: UploadResult[] }> {
  const fd = new FormData()
  for (const f of files) fd.append('file', f)
  return api.post('/api/uploads', { body: fd }).json<{ uploaded: UploadResult[] }>()
}

export interface IngestionStatus {
  queue: { queued: number; running: number; done: number; dead: number }
  documents: Record<string, number>
  candidates: number
}

export const getIngestionStatus = (): Promise<IngestionStatus> =>
  api.get('/api/ingestion/status').json<IngestionStatus>()

export interface CandidateRow {
  id: string
  name: string | null
  totalMonthsExp: number | null
  employer: string | null
  location: string | null
  noticeDays: number | null
  noticeBucket: string | null
  skills: string[]
}

export interface CandidatePage {
  candidates: CandidateRow[]
  total: number
  page: number
  pageSize: number
}

export type CandidateSort = 'recent' | 'expDesc' | 'expAsc' | 'name'

export interface CandidateFilters {
  search?: string
  skill?: string
  location?: string
  minExp?: number
  maxExp?: number
  sort?: CandidateSort
  page?: number
  pageSize?: number
}

export const listCandidates = (f: CandidateFilters = {}): Promise<CandidatePage> => {
  const searchParams: Record<string, string | number> = { pageSize: f.pageSize ?? 25, page: f.page ?? 0 }
  if (f.search) searchParams.search = f.search
  if (f.skill) searchParams.skill = f.skill
  if (f.location) searchParams.location = f.location
  if (f.minExp) searchParams.minExp = f.minExp
  if (f.maxExp) searchParams.maxExp = f.maxExp
  if (f.sort) searchParams.sort = f.sort
  return api.get('/api/candidates', { searchParams }).json<CandidatePage>()
}

export interface CandidateFacets {
  locations: { value: string; count: number }[]
  skills: string[]
}

export const getCandidateFacets = (): Promise<CandidateFacets> =>
  api.get('/api/candidates/facets').json<CandidateFacets>()

export interface CandidateDetail {
  id: string
  name: string | null
  email: string | null
  phone: string | null
  pan: string | null
  dob: string | null
  totalMonthsExp: number | null
  employer: string | null
  location: string | null
  preferredLocations: string[] | null
  currentCtc: string | null
  expectedCtc: string | null
  noticeDays: number | null
  parsedJson: unknown
}

export const getCandidate = (id: string): Promise<{ candidate: CandidateDetail } | { error: string }> =>
  api.get(`/api/candidates/${id}`).json<{ candidate: CandidateDetail } | { error: string }>()

export interface MatchRequest {
  mustHave: string[]
  niceToHave?: string[]
  minMonths?: number
  maxMonths?: number
  location?: string
  topN?: number
}

export interface RankedCandidate {
  candidateId: string
  name: string | null
  score: number
  matched: string[]
  niceHitCount: number
  totalMonthsExp: number | null
  location: string | null
  rationale: string
}

export const matchCandidates = (body: MatchRequest): Promise<{ count: number; shortlist: RankedCandidate[] }> =>
  api.post('/api/match', { json: body }).json<{ count: number; shortlist: RankedCandidate[] }>()

export interface CreateJobBody {
  title: string
  company?: string
  agencyJobRef?: string
  mustHave: string[]
  niceToHave?: string[]
  minMonths?: number
  maxMonths?: number
  location?: string
  ctcMaxLpa?: number
  noticeMaxDays?: number
  jdSpec?: unknown // full extracted requirement spec — carried from the JD parse so screening can score it
  jdRaw?: string
  jdFormatted?: string
  owner?: string
}

// Structured requirements extracted from a JD parse, carried from /api/jd/parse → createJob so the
// experience band / location / CTC / notice actually reach the job (not just the skills).
export interface ParsedRequirements {
  minMonths?: number
  maxMonths?: number
  location?: string
  ctcMaxLpa?: number
  noticeMaxDays?: number
  jdSpec?: unknown
}

export const createJob = (body: CreateJobBody): Promise<{ jobId: string; requirements: unknown }> =>
  api.post('/api/jobs', { json: body }).json<{ jobId: string; requirements: unknown }>()

// The live screening stream is consumed via EventSource directly (see the screen route),
// since EventSource is GET + same-origin (vite proxies /api → :8000, tenant defaults to inumellas).
export const screenStreamUrl = (jobId: string, tier?: string): string =>
  `/api/jobs/${jobId}/screen/stream${tier ? `?tier=${tier}` : ''}`

// Economy / overnight screening (Groq Batch — 50% cheaper, async ≤24h).
export interface BatchStatus { id: string; status: string; model: string | null; counts: { total?: number; completed?: number; failed?: number } | null; matchRunId: string | null; total: number; createdAt: string; updatedAt: string }
export const startBatchScreen = (jobId: string, body: { topN?: number; tier?: string } = {}) =>
  api.post(`/api/jobs/${jobId}/screen/batch`, { json: body }).json<{ screenBatchId: string; batchId: string; model: string; status: string; count: number; completionWindow: string }>()
export const getBatchScreen = (jobId: string) =>
  api.get(`/api/jobs/${jobId}/screen/batch`).json<{ batch: BatchStatus | null }>()

export type JobStage = 'sourcing' | 'screening' | 'shortlisted' | 'submitted' | 'interview' | 'placement' | 'archived'

export interface JobRow {
  id: string
  title: string
  company: string | null
  systemRef: string | null
  ref: string | null
  status: string
  stage: JobStage
  location: string | null
  owner: string | null
  createdAt: string
  screened: boolean
  lastScreenedAt: string | null
  screenedCount: number
  shortlistedCount: number
  mustHave: string[]
  niceToHave: string[]
}
export interface JobsPage {
  jobs: JobRow[]
  total: number
  page: number
  pageSize: number
}
export type JobSort = 'recent' | 'title'
export interface JobFilters {
  search?: string
  status?: 'all' | 'pending' | 'screened'
  company?: string
  stage?: JobStage | 'all'
  sort?: JobSort
  page?: number
  pageSize?: number
}
export const listJobs = (f: JobFilters = {}): Promise<JobsPage> => {
  const sp: Record<string, string | number> = { pageSize: f.pageSize ?? 25, page: f.page ?? 0 }
  if (f.search) sp.search = f.search
  if (f.status && f.status !== 'all') sp.status = f.status
  if (f.company) sp.company = f.company
  if (f.stage && f.stage !== 'all') sp.stage = f.stage
  if (f.sort) sp.sort = f.sort
  return api.get('/api/jobs', { searchParams: sp }).json<JobsPage>()
}

export interface UpdateJobBody {
  title?: string
  company?: string | null
  location?: string | null
  owner?: string | null
  stage?: JobStage
  mustHave?: string[]
  niceToHave?: string[]
}
export const updateJob = (id: string, body: UpdateJobBody): Promise<{ ok: boolean }> =>
  api.patch(`/api/jobs/${id}`, { json: body }).json<{ ok: boolean }>()
export const setJobStage = (id: string, stage: JobStage): Promise<{ ok: boolean; stage: JobStage; promoted?: number }> =>
  api.post(`/api/jobs/${id}/stage`, { json: { stage } }).json<{ ok: boolean; stage: JobStage; promoted?: number }>()
export const deleteJob = (id: string): Promise<{ ok: boolean }> =>
  api.delete(`/api/jobs/${id}`).json<{ ok: boolean }>()
export const getJobCompanies = (): Promise<{ companies: { value: string; count: number }[] }> =>
  api.get('/api/jobs/companies').json<{ companies: { value: string; count: number }[] }>()

export interface JobDetail {
  id: string
  requirements: {
    title: string
    mustHave: string[]
    niceToHave?: string[]
    location?: string
    minMonths?: number
    maxMonths?: number
    ctcMaxLpa?: number
    noticeMaxDays?: number
  }
  company: string | null
  systemRef: string | null
  jdRaw: string | null
  jdFormatted: string | null
  status: string
  stage: JobStage
  ref: string | null
  owner: string | null
  createdAt: string
  screened: boolean
  lastScreenedAt: string | null
  shortlist: JobShortlistCandidate[]
  // Cumulative funnel: count of candidates whose furthest delivery gate has reached each stage.
  stageCounts: Record<DeliveryStage, number>
}
// The five per-candidate delivery gates (cumulative — reaching one implies all earlier ones).
export type DeliveryStage = 'screening' | 'shortlisted' | 'submitted' | 'interview' | 'placement'
export const DELIVERY_STAGES: DeliveryStage[] = ['screening', 'shortlisted', 'submitted', 'interview', 'placement']
export const deliveryStageIndex = (s: string | null | undefined): number => {
  const i = DELIVERY_STAGES.indexOf((s ?? 'screening') as DeliveryStage)
  return i < 0 ? 0 : i
}
export interface SubScores { skills: number; allied: number; experience: number; notice: number; ctc: number; location: number }
export interface JobShortlistCandidate {
  candidateId: string
  name: string | null
  score: number | null
  verdict: string | null
  deliveryStage: DeliveryStage // furthest delivery gate this candidate has reached (the latest tag)
  selected: boolean
  rank: number | null
  rationale: string | null
  strengths: string[]
  concerns: string[]
  pointsToDiscuss: string[]
  confidence: string | null
  subScores: SubScores | null
  matched: string[]
  employer: string | null
  totalMonthsExp: number | null
  location: string | null
  skills: string[]
}
export const getJob = (id: string): Promise<JobDetail> => api.get(`/api/jobs/${id}`).json<JobDetail>()
export const selectCandidate = (jobId: string, candidateId: string, selected: boolean): Promise<{ ok: boolean; selected: boolean }> =>
  api.post(`/api/jobs/${jobId}/select`, { json: { candidateId, selected } }).json<{ ok: boolean; selected: boolean }>()

export interface ClientRow {
  name: string
  totalRoles: number
  openRoles: number
  screenedRoles: number
  shortlisted: number
  location: string | null
  lastActivity: string
}
export const listClients = (): Promise<{ clients: ClientRow[] }> => api.get('/api/clients').json<{ clients: ClientRow[] }>()

export interface ClientRole {
  id: string
  title: string
  systemRef: string | null
  ref: string | null
  status: string
  stage: JobStage
  screened: boolean
  shortlistCount: number
  createdAt: string
  mustHave: string[]
  niceToHave: string[]
}
export interface ClientDetail {
  name: string
  stats: { totalRoles: number; openRoles: number; screenedRoles: number; shortlisted: number; location: string | null }
  jobs: ClientRole[]
}
export const getClient = (name: string): Promise<ClientDetail> => api.get(`/api/clients/${encodeURIComponent(name)}`).json<ClientDetail>()

export type DashboardPeriod = 'week' | 'month' | 'quarter' | 'all'
export interface DashboardStats {
  period: string
  funnel: {
    totalPositions: number
    openPositions: number
    screened: number
    submitted: number | null
    interviews: number | null
    placements: number | null
  }
  roles: { open: number; newThisPeriod: number; clients: number | null }
  delivery: { screened: number; shortlisted: number; placements: number | null }
  talent: { totalCandidates: number; addedThisPeriod: number; avgFit: number | null }
  needsReview: { jobId: string; title: string; shortlistCount: number; screenedAt: string }[]
}
export const getDashboard = (period: DashboardPeriod = 'month'): Promise<DashboardStats> =>
  api.get('/api/dashboard', { searchParams: { period } }).json<DashboardStats>()

// --- Super-admin: AI cost / token ledger ---
export interface ModelUsage {
  provider: string | null
  model: string
  calls: number
  inputTokens: number
  outputTokens: number
  inputUsdPerM: number | null
  outputUsdPerM: number | null
  usd: number
  inr: number
  avgLatencyMs: number
}

export interface CreditUsage {
  inrPerCredit: number
  baseCreditsPerRecord: number
  targetMarkup: number
  perModel: Record<string, number>
  perRecordCredits: number
  perRecordInr: number
  suggestedCreditsPerRecord: number
  impliedMarkup: number | null
  recordsToDate: number
  creditsToDate: number
  revenueInrToDate: number
}

export interface UsageReport {
  fxRate: number
  byModel: ModelUsage[]
  totals: { calls: number; inputTokens: number; outputTokens: number; usd: number; inr: number }
  basis: 'measured' | 'estimated'
  perRecord: { inputTokens: number; outputTokens: number; usd: number; inr: number }
  perJob: { calls: number; avgTokens: number; usd: number; inr: number }
  projection: Array<{ records: number; usd: number; inr: number; model: string; credits: number; creditInr: number }>
  credits: CreditUsage
}

export const getAiUsage = (): Promise<UsageReport> => api.get('/api/admin/ai-usage').json<UsageReport>()

export interface AiCallRow {
  id: string
  createdAt: string
  task: string
  provider: string | null
  model: string
  inputTokens: number
  outputTokens: number
  records: number | null
  latencyMs: number | null
  cacheHit: boolean
  callCount: number // >1 = a bundled trigger (one screening = N per-candidate calls)
  jobRef: string | null // human-readable job this bundle refers to
  usd: number
  inr: number
  credits: number // client credits billed for this trigger
  creditInr: number // client ₹ billed = credits × inrPerCredit
}
export interface AiCallLogPage { rows: AiCallRow[]; total: number; limit: number; offset: number }
export const getAiCalls = (limit = 25, offset = 0): Promise<AiCallLogPage> =>
  api.get('/api/admin/ai-usage/calls', { searchParams: { limit, offset } }).json<AiCallLogPage>()

export const updateFxRate = (rate: number): Promise<{ fxRate: number }> =>
  api.put('/api/admin/settings/fx', { json: { rate } }).json<{ fxRate: number }>()

export const updateCreditModel = (
  patch: Partial<Pick<CreditUsage, 'inrPerCredit' | 'baseCreditsPerRecord' | 'targetMarkup' | 'perModel'>>,
): Promise<{ credits: CreditUsage }> => api.put('/api/admin/settings/credits', { json: patch }).json<{ credits: CreditUsage }>()

export interface ScreenedCandidate {
  candidateId: string
  name: string | null
  score: number
  verdict: 'strong' | 'maybe' | 'weak' | null
  rationale: string
  gaps: string[]
  totalMonthsExp: number | null
  location: string | null
  employer?: string | null
  skills?: string[]
}

export interface JdParseResult {
  company: string
  title: string
  mustHave: string[]
  niceToHave: string[]
  formatted?: string
  rawText?: string // the extracted JD body (from a file/URL) — kept so the description isn't dropped
  requirements?: ParsedRequirements // experience band / location / CTC / notice / jdSpec — carried into the job
  note?: string
}

export const parseJd = (text: string): Promise<JdParseResult> =>
  api.post('/api/jd/parse', { json: { text } }).json<JdParseResult>()

export const parseJdFile = (file: File): Promise<JdParseResult> => {
  const fd = new FormData()
  fd.append('file', file)
  return api.post('/api/jd/parse', { body: fd }).json<JdParseResult>()
}

// --- Control plane (automation modes + approvals inbox) ---
export type StageKey = 'jd_intake' | 'cv_ingest' | 'screening' | 'submission' | 'feedback_chase' | 'scheduling' | 'warming'
export type AutomationMode = 'auto' | 'review' | 'off'
export interface AutomationStage { key: StageKey; label: string; mode: AutomationMode }
export const getAutomation = (): Promise<{ stages: AutomationStage[] }> =>
  api.get('/api/automation').json<{ stages: AutomationStage[] }>()
export const setAutomation = (stage: StageKey, mode: AutomationMode): Promise<{ ok: boolean }> =>
  api.put('/api/automation', { json: { stage, mode } }).json<{ ok: boolean }>()

export interface ApprovalTask {
  id: string
  stage: StageKey
  title: string
  summary: string | null
  status: 'pending' | 'approved' | 'rejected'
  entityType: string | null
  entityId: string | null
  payload: unknown
  createdAt: string
}
export const getApprovals = (status: 'pending' | 'approved' | 'rejected' | 'all' = 'pending'): Promise<{ tasks: ApprovalTask[] }> =>
  api.get('/api/approvals', { searchParams: { status } }).json<{ tasks: ApprovalTask[] }>()
export const resolveApproval = (id: string, decision: 'approved' | 'rejected'): Promise<{ ok: boolean; decision: string; result: Record<string, unknown> }> =>
  api.post(`/api/approvals/${id}/resolve`, { json: { decision } }).json<{ ok: boolean; decision: string; result: Record<string, unknown> }>()

// --- Reflexes: autonomous intake ---
export interface JdIngestResult { mode: AutomationMode; jobId?: string; approvalId?: string; skipped?: boolean }
export const ingestJd = (text: string): Promise<JdIngestResult> =>
  api.post('/api/ingest/jd', { json: { text } }).json<JdIngestResult>()

export interface EmailIngestResult { candidateId: string; isNew: boolean; hadCv: boolean; name: string | null }
export const ingestEmail = (body: string, cv?: File, customFields?: Record<string, unknown>): Promise<EmailIngestResult> => {
  const fd = new FormData()
  fd.append('body', body)
  if (cv) fd.append('cv', cv)
  if (customFields) fd.append('customFields', JSON.stringify(customFields))
  return api.post('/api/ingest/email', { body: fd }).json<EmailIngestResult>()
}

export interface SubmissionDraft {
  jobId: string
  title: string
  company: string | null
  candidates: { handle: string; headline: string; skills: string[]; fit: number; verdict: string; why: string }[]
  coverNote: string
}
export interface SubmissionResult { mode: AutomationMode; approvalId?: string; draft?: SubmissionDraft; skipped?: boolean }
export const draftSubmission = (jobId: string): Promise<SubmissionResult> =>
  api.post(`/api/jobs/${jobId}/submission`, { json: {} }).json<SubmissionResult>()

// --- Ingestion: per-file progress tracking ---
export type DocumentStatus =
  | 'stored'
  | 'queued'
  | 'scanned'
  | 'extracting'
  | 'parsing'
  | 'processing'
  | 'review'
  | 'processed'
  | 'failed'
  | 'quarantined'
  | 'skipped'

export interface DocumentRow {
  id: string
  file_name: string
  kind: string | null
  status: DocumentStatus
  error: string | null
  meta: unknown
  candidate_id: string | null
  created_at: string
  processed_at: string | null
  downloadUrl?: string
}

export interface DocumentEvent {
  from_status: DocumentStatus | null
  to_status: DocumentStatus
  detail: Record<string, unknown> | null
  created_at: string
}

export const getRecentIngestion = (limit = 50): Promise<{ documents: DocumentRow[] }> =>
  api.get('/api/ingestion/recent', { searchParams: { limit } }).json<{ documents: DocumentRow[] }>()

export const getDocumentEvents = (id: string): Promise<{ events: DocumentEvent[] }> =>
  api.get(`/api/documents/${id}/events`).json<{ events: DocumentEvent[] }>()

// The /api/documents/:id/download endpoint requires the x-tenant header (injected by the ky
// instance). A plain href cannot carry custom headers, so we fetch the blob via ky and
// produce an object URL that can be opened in a new tab.
export const documentDownloadUrl = async (id: string): Promise<string> => {
  const blob = await api.get(`/api/documents/${id}/download`).blob()
  return URL.createObjectURL(blob)
}

// --- Coordination agent + public share-link ---
export interface CoordScanResult { created: number; touches: { touch: StageKey; jobId: string; title: string; mode: string }[] }
export const runCoordinationScan = (): Promise<CoordScanResult> =>
  api.post('/api/coordination/scan', { json: {} }).json<CoordScanResult>()

export interface ShareMaskedCandidate { handle: string; headline: string; skills: string[]; fit: number }
export interface ShareLinkView {
  kind: 'feedback' | 'schedule'
  title: string | null
  company: string | null
  status: 'open' | 'responded' | 'expired'
  payload: { candidates: ShareMaskedCandidate[] } | null
  response: unknown
}
// Public — no tenant/auth needed (token is the secret).
export const getShareLink = (token: string): Promise<ShareLinkView> =>
  api.get(`/api/share/${token}`).json<ShareLinkView>()
export const respondShareLink = (token: string, body: { verdicts?: { handle: string; decision: 'advance' | 'reject' }[]; slots?: string; note?: string }): Promise<{ ok: boolean }> =>
  api.post(`/api/share/${token}`, { json: body }).json<{ ok: boolean }>()
